/**
 * Configuration object containing the details required to correctly set up the header
 *
 * @typedef {Object} OverflowButtonMap
 * @property {string} key - An I18N Key
 * @property {jQuery} value - A jQuery element
 * -----
 * @typedef {Object} DefaultHeader~Configuration
 * @property {string} title - The title to be displayed in the header
 * @property {?jQuery[]} leftButtons - An array of jQuery elements to be added to the button container on the left side of the header
 * @property {?jQuery[]} rightButtons - An array of jQuery elements to be added to the button container on the right side of the header
 * @property {?OverflowButtonMap} overflowButtons - A map of jQuery elements to be added to the button container on the right side of the header
 *                                         which can end up in an overflow menu if there is limited space
 * @property {?string} additionalClasses - space-separated list of classes to be added to the header whilst this configuration is active
 * @property {?string} overflowClasses - space-separated list of classes to be added to the overflow menu whilst this configuration is active
 * @property {?string} contentClass - the "id" (data-nav-icon-class) of the view for which this header controls
 * @property {?jQuery[]} searchHeader - A jQuery form element to be added into the searchPanel
 *
 */

var PopupMenu = require("./PopupMenu");
var Utils = require("./Utils");

/**
 * Base class for all headers in the UI
 *
 * @class
 */
var DefaultHeader = function() {
    var additionalClasses;
    var overflowClasses;
    var viewSelector;

    var $header = $('<div data-role="header" data-theme="b" class="headerPanel" data-tap-toggle="false" data-update-page-padding="false">');
    var $headerContents = $("<div>");
    var $searchHeader = $('<div class="searchPanel inlineSearch">');
    var $title = $('<h1 class="ui-title" data-i18n="">');
    var $leftButtons = $('<div data-type="horizontal" data-role="controlgroup" class="ui-btn-left icon-only">');
    var $rightButtons = $('<div data-type="horizontal" data-role="controlgroup" class="ui-btn-right icon-only">');
    var rightButtonArray = [];
    var currentWidth = 0;

    var overflowButtonMap = {};
    var overflowMenu = new PopupMenu("overflowMenuContainer", true, "right");
    overflowMenu.getButton().addClass("overflowButton");
    $("#mobileResources").append(overflowMenu.getContent());

    $header.append($headerContents);
    $headerContents.append($leftButtons)
      .append($title)
      .append($rightButtons);

    /**
     * Return the header content as a jQuery-wrapped element
     *
     * @returns {jQuery} jQuery element containing header content
     */
    this.getContent = function() {
        return $header;
    };

    /**
     * Sets the configuration of the header (title, buttons, etc.) based on the object provided.
     * @param {DefaultHeader~Configuration} config - the configuration object to be used
     */
    this.configure = function(config) {
        // Remove any classes added by the previous configuration
        $header.removeClass(additionalClasses);
        overflowMenu.getContent().removeClass(overflowClasses);
        overflowMenu.getButton().removeClass(overflowClasses);

        // Set the title
        $title.text(config.title);

        // Set the i18n attribute, if applicable
        if (config.i18n) {
            $title.attr("data-i18n", config.i18n);
        }

        // Add the buttons on the left
        $leftButtons.children().detach();
        var leftButtons = config.leftButtons || [];
        leftButtons.forEach(function(btn) {
            $leftButtons.append(btn);
        });

        // Add the buttons on the right
        $rightButtons.children().detach();
        rightButtonArray = config.rightButtons || [];
        rightButtonArray.forEach(function(btn) {
            $rightButtons.append(btn);
        });

        overflowButtonMap = config.overflowButtons || {};

        for (var key in overflowButtonMap) {
            if (overflowButtonMap.hasOwnProperty(key)) {
                $rightButtons.append(overflowButtonMap[key]);
            }
        }

        // Add any additional classes specified in the config
        additionalClasses = config.additionalClasses;
        overflowClasses = config.overflowClasses;
        $header.addClass(additionalClasses);
        overflowMenu.getContent().addClass(overflowClasses);
        overflowMenu.getButton().addClass(overflowClasses);

        setViewSelector(config.contentClass);

        // Remove any previous search headers
        $searchHeader.children().detach();
        $searchHeader.remove();
        hideViewSearchBar();

        if (config.searchHeader) {
            $headerContents.append($searchHeader);
            $searchHeader.append(config.searchHeader);
        }
        layoutHeader();

        // Make sure the header has been jQueryMobile-ized
        $header.enhanceWithin();
    };

    /**
     * Makes the header content visible and ensures that the width of the title is restricted sufficiently to
     * avoid collisions with the left/right buttons
     */
    this.show = function() {
        $header.show();
        layoutHeader();
    };

    /**
     * Hides the header content
     */
    this.hide = function() {
        $header.hide();
    };

    /**
     * Called whenever the browser is re-sized
     */
    this.resize = function(width) {
        if (width == currentWidth) {
            // If the width hasn't changed, then we don't need to recalculate the header
            return;
        }
        currentWidth = width;
        layoutHeader();
    };

    function layoutHeader() {
        layoutSearchBar();
        removeOverflowMenu();

        var hasOverflowButtons = false;

        for (var key in overflowButtonMap) {
            if (overflowButtonMap.hasOwnProperty(key)) {
                hasOverflowButtons = true;
                break;
            }
        }

        if (hasOverflowButtons) {
            if (($searchHeader.children().length > 0 && $(viewSelector + " .searchPanel > form").length > 0) || $searchHeader.children().length == 0) {
                // The search header is in the view so we are dealing with a small screen or there is no search header.
                // Determine whether we need to put any buttons in an overflow menu
                var $slickIcons = $header.find(".slick-dots li");
                var calcTitleWidth = Math.max(titleWidth($title), $slickIcons.outerWidth() * $slickIcons.size()) * 1.05;

                if (($header.outerWidth() / 2) - (calcTitleWidth / 2) - ($rightButtons.outerWidth()) < 0) {
                    addOverflowMenu();
                }
            }
        }

        // We need to add the same margin to both sides to keep things correctly centred
        var marginWidth = Math.max($leftButtons.outerWidth(), $rightButtons.outerWidth());

        // Apply the margin to everything in the header that isn't in a button container (titles, nav dots etc.)
        var elements = $header.find("> :not([data-role=controlgroup]):not([data-role=popup])");

        // Apply the margins
        elements.css("margin-left", marginWidth)
          .css("margin-right", marginWidth);
    }

    function addOverflowMenu() {
        $rightButtons.children().detach();
        rightButtonArray.forEach(function(btn) {
            $rightButtons.append(btn);
        });
        $rightButtons.append(overflowMenu.getButton());
        $header.enhanceWithin();

        overflowMenu.clear();
        var size = Utils.objectLength(overflowButtonMap);

        for (var key in overflowButtonMap) {
            if (overflowButtonMap.hasOwnProperty(key)) {
                var button = overflowButtonMap[key];
                var entry = overflowMenu.addEntry($.t(key), button.click.bind(button), size * 10, button.attr("data-icon"));
                Utils.setElementEnabled(entry, !button.prop("disabled"));
                size--;
            }
        }
        overflowMenu.refresh();
        overflowMenu.hide();
    }

    function removeOverflowMenu() {
        if ($('a[data-icon="mobile-overflow"]').length > 0) {
            overflowMenu.getButton().detach();

            for (var key in overflowButtonMap) {
                if (overflowButtonMap.hasOwnProperty(key)) {
                    $rightButtons.append(overflowButtonMap[key]);
                }
            }
        }
    }

    function setViewSelector(contentClass) {
        viewSelector = 'div[data-nav-icon-class="' + contentClass + '"]';
    }

    function hideViewSearchBar() {
        var $searchPanel = $(viewSelector + " .searchPanel");

        if ($searchPanel.length > 0) {
            $searchPanel.hide();
        }
    }

    function layoutSearchBar() {
        // If the search form is currently in the view, move it back into the header
        if ($(viewSelector + " .searchPanel > form").length > 0) {
            $searchHeader.append($(viewSelector + " .searchPanel > form").detach());
            $(viewSelector + " .searchPanel").hide();
        }
        var remainingSpace = $header.outerWidth() / 2;
        var $slickIcons = $header.find(".slick-dots li");

        // The slick icons block is centred so we only care about the right half
        remainingSpace -= ($slickIcons.outerWidth() * $slickIcons.length) / 2;
        var rightWidth = $rightButtons.outerWidth();
        remainingSpace -= rightWidth;

        if (remainingSpace > $searchHeader.outerWidth() * 1.05) {
            $searchHeader.css("right", rightWidth + "px");
            $searchHeader.show();
        } else {
            var $searchForm = $searchHeader.children().detach();
            $searchHeader.hide();

            if ($(viewSelector + " .searchPanel").length == 0) {
                $(viewSelector).prepend($('<div class="searchPanel searchBar">'));
            } else {
                $(viewSelector + " .searchPanel").show();
            }
            $(viewSelector + " .searchPanel").append($searchForm);
        }
    }

    function titleWidth(titleElement) {
        var testElement = $('<span style="position : absolute; width : auto; left : -9999px">' + titleElement.html() + "</span>");
        testElement.css("font-family", titleElement.css("font-family"));
        testElement.css("font-size", titleElement.css("font-size"));
        testElement.css("font-size", titleElement.css("font-weight"));

        $header.append(testElement);
        var width = testElement.width();
        testElement.remove();
        return width;
    }
};

module.exports = DefaultHeader;
